/**
 * Las fechas de las bandejas del panel, en el idioma de quien mira.
 *
 * ── Por qué no hay un `Intl` de módulo ──────────────────────────────────────
 * Antes sí lo había, con `'es-MX'` escrito dentro. Un formateador creado al
 * cargar el archivo se queda con el idioma que hubiera en ese momento para toda
 * la sesión, y como esto lo llaman seis bandejas —Páginas, Formularios,
 * Colecciones, Ofertas, Pedidos y la ficha de un cobro— el resultado era una
 * columna de fechas en español dentro de un panel en inglés. Se construyen por
 * idioma y se guardan en un mapa: `Intl` es caro de crear y barato de reusar,
 * pero lo que no puede es fijarse una sola vez.
 *
 * El idioma sale de `activeLanguage()` y no de un parámetro por la misma razón
 * que `moneyLocale()` en `region.tsx`: esto se llama desde celdas de tabla y
 * funciones sueltas que no son componentes y no pueden usar hooks.
 */
import { CATALOGS, createTranslator, type Language } from '@webbuilder/i18n'
import { activeLanguage } from './i18n'

/** El locale de `Intl` para cada idioma. Sin variante regional: la fecha corta
 *  de una lista se escribe igual en México que en España. */
const INTL_LOCALE: Record<Language, string> = { es: 'es', en: 'en' }

type ListFormatters = { day: Intl.DateTimeFormat; time: Intl.DateTimeFormat }

const cache = new Map<Language, ListFormatters>()

function formattersFor(language: Language): ListFormatters {
  const cached = cache.get(language)
  if (cached) return cached
  const locale = INTL_LOCALE[language]
  const made: ListFormatters = {
    day: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
    /* Reloj de 24 horas en los dos idiomas. En 'es' el formato por defecto es
       de 12 con «a.m.» y «p.m.» detrás: cuatro caracteres más por fila que
       además cambian de ancho según la hora, así que la columna nunca cuadra
       consigo misma. En inglés pasa lo mismo con «AM»/«PM», y una tabla que
       cambia de ancho al cambiar de idioma es peor que una convención menos
       familiar. */
    time: new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
  }
  cache.set(language, made)
  return made
}

/** Días naturales entre dos fechas, sin contar las horas de por medio. */
function daysApart(from: Date, to: Date): number {
  const day = 24 * 60 * 60 * 1000
  const startOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  return Math.round((startOf(to) - startOf(from)) / day)
}

function parse(value: string | number | Date | undefined): Date | null {
  if (value === undefined) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isFinite(date.getTime()) ? date : null
}

/**
 * Fecha con hora sólo mientras la hora dice algo.
 *
 * Mirando cuándo se tocó algo por última vez, la pregunta cambia con el tiempo:
 * de lo de hoy y lo de ayer se quiere saber a qué hora —para reconocer si es lo
 * que se acaba de hacer—, y de lo de la semana pasada, no: ahí «11:10» es ruido
 * que además alarga la columna. Dos días es donde deja de importar.
 */
export function formatDashboardListDateTime(value: string | number | Date | undefined): string {
  const date = parse(value)
  if (!date) return '—'

  const language = activeLanguage()
  const { day, time } = formattersFor(language)
  const days = daysApart(date, new Date())
  if (days > 1) return day.format(date)

  const t = createTranslator(language, CATALOGS)
  const key = days === 0 ? 'common.listDate.today' : 'common.listDate.yesterday'
  return t(key, { time: time.format(date) })
}

/** Fecha corta y hora explícita, incluso en registros antiguos.
 *
 * Las tarjetas de actividad son un historial: ahí la hora no pierde relevancia
 * al pasar los días, porque permite ordenar y distinguir sucesos del mismo día.
 */
export function formatDashboardListDateWithTime(value: string | number | Date | undefined): string {
  const date = parse(value)
  if (!date) return '—'

  const { day, time } = formattersFor(activeLanguage())
  return `${day.format(date)} · ${time.format(date)}`
}

/** Fecha compacta compartida por las bandejas del dashboard. */
export function formatDashboardListDate(value: string | number | Date | undefined): string {
  const date = parse(value)
  if (!date) return '—'

  const language = activeLanguage()
  const { day, time } = formattersFor(language)
  const today = new Date()
  const isToday = date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate()
  if (!isToday) return day.format(date)

  return createTranslator(language, CATALOGS)('common.listDate.today', { time: time.format(date) })
}

/**
 * Sólo el día corto —«15 sept 2026»—, sin «Hoy» ni hora.
 *
 * Para cuando la fecha va detrás de algo que ya dice lo reciente que es:
 * «hace dos horas · 15 sept 2026». Con `formatDashboardListDate` eso saldría
 * «hace dos horas · Hoy, 16:40», la misma información dicha dos veces.
 */
export function formatDashboardListDay(value: string | number | Date | undefined): string {
  const date = parse(value)
  if (!date) return '—'
  return formattersFor(activeLanguage()).day.format(date)
}
